/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import {
  Autocomplete,
  createFilterOptions,
  FilterOptionsState,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { FormChangeEventHandler } from "../../types/UseForm";
import validatorStyle from "../Validator/validatorStyle";
import { ValidatorType } from "../../types/ValidatorType";

type AppendableListFieldProps<T extends Record<string, string>> = {
  label: string;
  name: string;
  value: T[];
  onChange: FormChangeEventHandler;
  helperText?: string;
  isHighlighted?: boolean;
  valueKey: keyof T;
  validators?: Array<ValidatorType>;
  initialDependentValidationFields?: Array<string>;
  registerValidators?: Function;
  inputValidator?: Array<{ validator: (value: string) => boolean; message: string }>;
  error?: boolean;
};

export default function AppendableListField<T extends Record<string, string> = Record<string, string>>({
  label,
  name,
  helperText,
  value,
  onChange,
  isHighlighted,
  valueKey,
  validators,
  registerValidators,
  initialDependentValidationFields,
  inputValidator,
  error
}: AppendableListFieldProps<T>) {
  const [inputValue, setInputValue] = useState<Partial<T>>({});
  const [errorText, setErrorText] = useState<string | null>(null);
  const hasError = Boolean(errorText || error);

  useEffect(() => {
    registerValidators && validators && registerValidators(name, validators, initialDependentValidationFields);
    return () => {
      registerValidators && registerValidators(name, []);
    };
  }, []);

  const filter = createFilterOptions<string>({
    matchFrom: "start",
    stringify: (option) => option ?? "",
  });

  const isNewValue = (val?: string) => !Boolean(value.find((savedOption) => savedOption[valueKey] === val));

  const handleChange = (event: SyntheticEvent<Element, Event>, newValue: string) => {
    if (
      inputValidator
        ?.map(({ validator, message }) => {
          if (!validator(newValue)) {
            setErrorText(message);
            return true;
          }
        })
        .includes(true)
    ) {
      return;
    }

    if (!isNewValue(newValue ?? "")) {
      setErrorText("Dieser Wert existiert bereits.");
      return;
    }

    const inputValue = { [valueKey]: newValue } as T;

    setErrorText(null);
    setInputValue(inputValue);
    onChange({
      target: {
        name,
        value: [...value, inputValue],
      },
    });
  };

  const filterOptions = (options: string[], params: FilterOptionsState<string>) => {
    const filtered = filter(options, params);

    const { inputValue } = params;
    const isExisting = options.some((option) => inputValue === option);
    if (inputValue !== "" && !isExisting) {
      filtered.push(inputValue);
    }

    return filtered;
  };

  return (
    <FormControl fullWidth error={hasError} sx={isHighlighted ? validatorStyle.mandatory : undefined}>
      <Autocomplete<string, false, true, true>
        onChange={handleChange}
        getOptionLabel={(option) => {
          if (!option) {
            return "";
          }

          return option;
        }}
        filterOptions={filterOptions}
        freeSolo
        disableClearable
        getOptionDisabled={(option) => !isNewValue(option)}
        renderInput={(params) => <TextField {...params} label={label} error={hasError} />}
        value={inputValue[valueKey] ?? ""}
        options={value.map((opt) => opt[valueKey])}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              <Typography component={"span"}>{option}</Typography>
              {isNewValue(option) && <Typography component={"span"}>&nbsp;hinzufügen</Typography>}
            </li>
          );
        }}
      />
      {(hasError || helperText) && <FormHelperText>{errorText || helperText}</FormHelperText>}
    </FormControl>
  );
}
